/**
 * Care-flow component types (Sprint 4.3.4, PROD-653).
 *
 * Awell stores a care flow as a sum of components serialised to JSON
 * files in a fixed 16-folder structure. This module models that surface
 * as TypeScript types so the editor + serializer + JSON detail view can
 * round-trip pathway state to and from the canonical folder layout.
 *
 * Source: vault `03-knowledge-base/external-references/awell/studio/
 * docs/source-control/concepts/components.md` (mirrored from
 * `https://developers.awellhealth.com/awell-studio/docs/source-control/
 * concepts/components`). Quoted in the sprint brief:
 *   "Components are stored in a folder structure as JSON files. 16
 *   component types: actions / api_calls / data_point_definitions /
 *   forms / labels / messages / pathways / questions / rules / settings
 *   / stakeholders / steps / timings / tracks / transitions / triggers"
 *
 * This is a PROTOTYPE-PHASE shape; per the source disclaimer the Awell
 * folder structure is experimental and may change. The Care Pathway
 * Studio prototype mirrors the current shape so the user-facing
 * 16-folder left-rail + JSON detail view stays anchored to the source.
 *
 * HC1 reproducibility: every component carries a stable `id`, optional
 * `version` + `lastModified` strings (ISO-8601 zero-clock when defaults
 * are used by the serializer); no clocks, no randomness.
 *
 * HC9 strict registry: the closed `CARE_FLOW_FOLDERS` tuple is the
 * single source of truth for folder names. The publish workflow refuses
 * to emit a folder absent from this tuple.
 *
 * HC5: zero em / en dashes anywhere.
 */

/**
 * Closed 16-folder vocabulary. Order is the canonical alphabetical
 * order from the Awell source so deterministic serialisation lands the
 * folders in the same sequence on every publish.
 */
export const CARE_FLOW_FOLDERS = [
  'actions',
  'api_calls',
  'data_point_definitions',
  'forms',
  'labels',
  'messages',
  'pathways',
  'questions',
  'rules',
  'settings',
  'stakeholders',
  'steps',
  'timings',
  'tracks',
  'transitions',
  'triggers',
] as const;

export type CareFlowFolder = (typeof CARE_FLOW_FOLDERS)[number];

/** Human-readable folder labels surfaced in the editor left-rail. */
export const CARE_FLOW_FOLDER_LABELS: Record<CareFlowFolder, string> = {
  actions: 'Actions',
  api_calls: 'API calls',
  data_point_definitions: 'Data point definitions',
  forms: 'Forms',
  labels: 'Labels',
  messages: 'Messages',
  pathways: 'Pathways',
  questions: 'Questions',
  rules: 'Rules',
  settings: 'Settings',
  stakeholders: 'Stakeholders',
  steps: 'Steps',
  timings: 'Timings',
  tracks: 'Tracks',
  transitions: 'Transitions',
  triggers: 'Triggers',
};

/**
 * Folder-level hints anchored to the Awell source. Surfaced inline in
 * the left-rail so the user knows what each folder contains without
 * leaving the editor.
 */
export const CARE_FLOW_FOLDER_HINTS: Record<CareFlowFolder, string> = {
  actions: 'User and system building blocks. Forms, calculations, API calls.',
  api_calls: 'External API call definitions referenced from actions.',
  data_point_definitions: 'Typed data points captured during the care flow.',
  forms: 'Patient and clinician forms used for input or interaction.',
  labels: 'Categorisation tags applied to other components.',
  messages: 'Communication templates dispatched during the flow.',
  pathways: 'High-level structure: tracks, steps, transitions.',
  questions: 'Questions used inside forms or decision-making.',
  rules: 'Conditional logic governing transitions and branches.',
  settings: 'Care-flow-wide configuration values.',
  stakeholders: 'User roles or recipients involved in the flow.',
  steps: 'Sequenced units inside a track. Connected by transitions.',
  timings: 'Time-based scheduling and triggers.',
  tracks: 'Declarative containers activated by triggers.',
  transitions: 'State transitions between steps.',
  triggers: 'Events that initiate or advance the flow.',
};

/**
 * Folders containing SHARED components per
 * `awell/studio/docs/getting-started/care-flow-components.mdx` and
 * `components.md`. Shared components can be reused across pathways;
 * editing one affects every pathway that cites it. The editor surfaces
 * a shared-component badge plus a warning prompt before editing a
 * shared component.
 */
export const SHARED_COMPONENT_FOLDERS: ReadonlySet<CareFlowFolder> = new Set([
  'forms',
  'data_point_definitions',
]);

/** Predicate: is a given folder a SHARED-component folder? */
export function isSharedFolder(folder: CareFlowFolder): boolean {
  return SHARED_COMPONENT_FOLDERS.has(folder);
}

/**
 * The canonical zero-clock timestamp used as the default
 * `lastModified` value in the serializer. Per HC1 reproducibility, the
 * serializer never reads `Date.now()`; callers wishing to record a
 * real edit time MUST pass it through explicitly.
 */
export const ZERO_CLOCK_ISO = '1970-01-01T00:00:00.000Z';

/**
 * Universal component-instance fields. Every concrete component shape
 * extends this base; the editor list-view renders id + label + version
 * + lastModified for every folder.
 */
export interface BaseComponentJson {
  id: string;
  label: string;
  version: string;
  lastModified: string;
  usedByPathwayIds?: ReadonlyArray<string>;
}

export interface ActionComponentJson extends BaseComponentJson {
  category: 'user' | 'system';
  formId?: string;
  apiCallId?: string;
  stepId?: string;
}

export interface ApiCallComponentJson extends BaseComponentJson {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  description?: string;
}

export interface DataPointDefinitionComponentJson extends BaseComponentJson {
  valueType: 'string' | 'number' | 'boolean' | 'date' | 'codeable-concept';
  codingSystem?: string;
  code?: string;
}

export interface FormComponentJson extends BaseComponentJson {
  questionIds: ReadonlyArray<string>;
  stakeholderId?: string;
}

export interface LabelComponentJson extends BaseComponentJson {
  category?: string;
}

export interface MessageComponentJson extends BaseComponentJson {
  channel: 'email' | 'sms' | 'in-app' | 'voice';
  body: string;
  recipientStakeholderId?: string;
}

export interface PathwayComponentJson extends BaseComponentJson {
  trackIds: ReadonlyArray<string>;
  triggerIds?: ReadonlyArray<string>;
  tenantId: string;
}

export interface QuestionComponentJson extends BaseComponentJson {
  questionType:
    | 'short-text'
    | 'long-text'
    | 'single-choice'
    | 'multi-choice'
    | 'number'
    | 'date'
    | 'boolean'
    /** Sprint 4.3.9: scale (integer range) authored on form actions. */
    | 'scale';
  required?: boolean;
  /**
   * Sprint 4.3.9: legacy string-only option list. Retained for backwards
   * compatibility with the pre-form-builder fixture shape.
   */
  options?: ReadonlyArray<string>;
  /**
   * Sprint 4.3.9: structured option list authored on form actions. Each
   * entry pairs a machine value with a user-facing label per the
   * `FormQuestionOption` domain type.
   */
  optionEntries?: ReadonlyArray<{ value: string; label: string }>;
  /**
   * Sprint 4.3.9: optional canonical id of the `data_point_definition`
   * the answer binds to. Mirrors the FHIR `Questionnaire.item.definition`
   * pointer at the JSON-bundle level.
   */
  dataPointDefinitionId?: string;
  /** Sprint 4.3.9: scale lower bound for `questionType === 'scale'`. */
  scaleMin?: number;
  /** Sprint 4.3.9: scale upper bound for `questionType === 'scale'`. */
  scaleMax?: number;
  /**
   * Sprint 4.3.10: optional pointer to a `RuleComponentJson` that gates
   * the question's display per the Awell `enableWhen` semantics.
   */
  ruleId?: string;
}

/**
 * Sprint 4.3.10: closed vocabulary of rule operators surfaced in the
 * 16-folder bundle. Mirrors the `RuleOperator` domain type so a rule
 * round-trips through the serializer without lossy coercion.
 */
export type RuleComponentOperator =
  | 'equals'
  | 'not-equals'
  | 'greater-than'
  | 'less-than'
  | 'greater-than-or-equals'
  | 'less-than-or-equals'
  | 'in'
  | 'not-in'
  | 'is-empty'
  | 'is-not-empty';

/** Sprint 4.3.10: closed Scrips risk-tier vocabulary on a rule. */
export type RuleComponentRiskTier = 'standard' | 'elevated' | 'critical';

/**
 * Sprint 4.3.10: structured rule attachment surface. A rule travels
 * verbatim in the bundle so the inverse pass reconstructs the operator,
 * operand, value, and risk tier without inference.
 *
 *   attachedTo               where the rule lives in the pathway model.
 *                              `transition`  a step's outbound transition
 *                                            evaluated at step
 *                                            advancement.
 *                              `form-question` conditional display logic
 *                                            on a form question.
 *   attachedToId             id of the host node (step or form-question).
 *   formQuestionParentNodeId id of the form-action node when
 *                            `attachedTo === 'form-question'`. Absent for
 *                            transition rules.
 *   dataPointDefinitionId    canonical operand id.
 *   operator                 closed RuleComponentOperator vocabulary.
 *   value                    right-hand operand. Ignored for
 *                            `is-empty` / `is-not-empty`.
 *   riskTier                 optional Scrips risk tier flag.
 *   expression               human-readable FHIRPath-style mirror of the
 *                            operator + operand + value, retained for
 *                            backwards compatibility with the pre-Sprint-
 *                            4.3.10 shape (a free-text expression).
 */
export interface RuleComponentJson extends BaseComponentJson {
  expression: string;
  attachedTo?: 'transition' | 'form-question';
  attachedToId?: string;
  formQuestionParentNodeId?: string;
  dataPointDefinitionId?: string;
  operator?: RuleComponentOperator;
  value?: string | number | ReadonlyArray<string>;
  riskTier?: RuleComponentRiskTier;
}

export interface SettingComponentJson extends BaseComponentJson {
  key: string;
  value: string | number | boolean | null;
}

export interface StakeholderComponentJson extends BaseComponentJson {
  role: 'patient' | 'practitioner' | 'caregiver' | 'system';
}

export interface StepComponentJson extends BaseComponentJson {
  trackId: string;
  actionIds: ReadonlyArray<string>;
  isDecision?: boolean;
}

export interface TimingComponentJson extends BaseComponentJson {
  kind: 'delay' | 'schedule' | 'recurring';
  expression: string;
}

export interface TrackComponentJson extends BaseComponentJson {
  pathwayId: string;
  stepIds: ReadonlyArray<string>;
  triggerType?: string;
  triggerFilter?: string;
}

export interface TransitionComponentJson extends BaseComponentJson {
  fromStepId: string;
  toStepId: string;
  ruleId?: string;
}

export interface TriggerComponentJson extends BaseComponentJson {
  kind: 'manual' | 'scheduled' | 'api' | 'patient-event';
  configuration?: Record<string, unknown>;
}

export type CareFlowComponentJson =
  | ActionComponentJson
  | ApiCallComponentJson
  | DataPointDefinitionComponentJson
  | FormComponentJson
  | LabelComponentJson
  | MessageComponentJson
  | PathwayComponentJson
  | QuestionComponentJson
  | RuleComponentJson
  | SettingComponentJson
  | StakeholderComponentJson
  | StepComponentJson
  | TimingComponentJson
  | TrackComponentJson
  | TransitionComponentJson
  | TriggerComponentJson;

export type ComponentJsonByFolder = {
  actions: ActionComponentJson;
  api_calls: ApiCallComponentJson;
  data_point_definitions: DataPointDefinitionComponentJson;
  forms: FormComponentJson;
  labels: LabelComponentJson;
  messages: MessageComponentJson;
  pathways: PathwayComponentJson;
  questions: QuestionComponentJson;
  rules: RuleComponentJson;
  settings: SettingComponentJson;
  stakeholders: StakeholderComponentJson;
  steps: StepComponentJson;
  timings: TimingComponentJson;
  tracks: TrackComponentJson;
  transitions: TransitionComponentJson;
  triggers: TriggerComponentJson;
};

export type CareFlowComponentBundle = {
  [F in CareFlowFolder]: ReadonlyArray<ComponentJsonByFolder[F]>;
};

/** Build an empty bundle with every folder present + empty. */
export function emptyCareFlowBundle(): CareFlowComponentBundle {
  return {
    actions: [],
    api_calls: [],
    data_point_definitions: [],
    forms: [],
    labels: [],
    messages: [],
    pathways: [],
    questions: [],
    rules: [],
    settings: [],
    stakeholders: [],
    steps: [],
    timings: [],
    tracks: [],
    transitions: [],
    triggers: [],
  };
}
